import React, { Fragment } from "react";
import {
  Datagrid,
  Filter,
  TextField,
  SearchInput,
  FunctionField,
  ListProps,
  ReferenceField,
  DateInput,
  TopToolbar,
  SelectInput,
} from "react-admin";
import StatusField from "components/StatusField";
import CustomDateField from "components/CustomDateField";
import BulkActionButton from "components/BulkActionButton";
import moment from "moment";
import { SchoolIdSelected } from "utils/tools";
import CustomPagination from "components/CustomPagination";
import BusScheduleByBusSchedulePlanInput from "pages/notTakingReports/components/BusScheduleByBusSchedulePlanInput";
import ListShowButton, { StyledButton } from "../../components/ListShowButton";
import ListWrapper from "../../components/ListWrapper";
import ActionsMenu from "../../components/ActionsMenu";
import AssignmentReportButton from "./components/AssignmentReportButton";
import UpdatedByField from "../../components/UpdatedByField";
import CustomTimeField from "../../components/CustomTimeField";
import CustomBooleanField from "../../components/CustomBooleanField";
import DriverWithReplacementIconField from "./components/DriverWithReplacementIconField";
import BusAssistantWithReplacementIconField from "./components/BusAssistantWithReplacementIconField";
import { availableOnlySuperAdmin } from "utils/validation";

const AssignmentFilter = (props: any) => (
  <Filter {...props}>
    <SearchInput source="search" alwaysOn />
    <DateInput source="EstimatedStartedAt" label="Date" alwaysOn />
    <SelectInput
      label="Assignment Status"
      source="Status"
      alwaysOn
      choices={[
        { id: "Ready", name: "Ready" },
        { id: "Started", name: "Started" },
        { id: "Ended", name: "Ended" },
        { id: "ForceEnded", name: "Force Ended" },
        { id: "EndedByAdmin", name: "Ended By Admin" },
        { id: "Cancelled", name: "Cancelled" },
        { id: "Expired", name: "Expired" },
      ]}
    />
    <SelectInput
      label="Arrival Status"
      source="ArrivalStatus"
      alwaysOn
      choices={[
        { id: "BusOnTime", name: "Bus on Time" },
        { id: "BusLate", name: "Bus Late" },
      ]}
    />
    <BusScheduleByBusSchedulePlanInput
      source="BusSchedulePlanId"
      schoolId={SchoolIdSelected()}
      label="Wave"
      allowEmpty
      alwaysOn
    />
  </Filter>
);

const BulkActionButtons = (props: any) => (
  <Fragment>
    {/* <BulkActionButton
      action="draft"
      label="Set Draft"
      resource="assignments"
      {...props}
    /> */}
    <BulkActionButton
      action="ready"
      label="Set Ready"
      resource="assignments"
      {...props}
    />
    <BulkActionButton
      action="cancelled"
      label="Set Cancel"
      resource="assignments"
      {...props}
    />
    <BulkActionButton
      action="started"
      label="Set Start"
      resource="assignments"
      {...props}
    />
    <BulkActionButton
      action="ended"
      label="Set End"
      resource="assignments"
      {...props}
    />

    {/* <BulkActionButton
      action="delete"
      label="DELETE"
      resource="assignments"
      {...props}
    /> */}
  </Fragment>
);

const ListActions = () => {
  return (
    <TopToolbar>
      <AssignmentReportButton />
    </TopToolbar>
  );
};

const AssignmentList = (props: ListProps) => {
  const TimesheetShowButton = (record: IAssignment) => {
    return (
      <StyledButton
        variant="outlined"
        href={`/#/subcontractorTimesheets?filter=${JSON.stringify({
          AssignmentId: record.id,
          Date: moment().format("Y-MM-DD"),
        })}`}
      >
        Timesheets
      </StyledButton>
    );
  };

  const breadcrumb = [{ link: "/assignments", title: "Bus Services" }];

  return (
    <>
      <ListWrapper
        breadcrumb={breadcrumb}
        headerTitle="Bus Services"
        exporter={false}
        hasCreate={false}
        filters={<AssignmentFilter />}
        filterDefaultValues={{
          EstimatedStartedAt: `${moment().format("Y-MM-DD")}`,
        }}
        filter={{
          SchoolId: Number(SchoolIdSelected()),
        }}
        bulkActionButtons={<BulkActionButtons />}
        actions={
          availableOnlySuperAdmin() ? (
            <ListActions />
          ) : null
        }
        title={"Bus Services"}
        perPage={50}
        sort={{ field: "routeName", order: "ASC" }}
        {...props}
        pagination={<CustomPagination />}
        denseMode
      >
        <Datagrid>
          <TextField source="routeName" emptyText="-" />
          <ReferenceField
            source="vehicleId"
            label="Vehicle"
            reference="vehicles"
            emptyText="-"
            link="show"
          >
            <TextField source="plateNumber" />
          </ReferenceField>
          <DriverWithReplacementIconField label="Driver" />
          <BusAssistantWithReplacementIconField label="Bus Assistant" />
          <FunctionField
            label="Start Details"
            render={(record: any) => {
              const { startedAt, startedBy } = record;

              // If both startedAt and startedBy are null, show a "-"
              if (!startedAt && !startedBy) {
                return <>-</>;
              }

              return (
                <Fragment>
                  {/* Show 'by {user}' if only startedBy is available */}
                  {startedBy && (
                    <>
                      by{" "}
                      <UpdatedByField
                        data={startedBy}
                        referenceSource="startedBy.id"
                      />
                    </>
                  )}

                  {/* Add a space between 'by {user}' and 'at {datetime}' only if both are available */}
                  {startedBy && startedAt && " "}

                  {/* Show 'at {datetime}' if only startedAt is available */}
                  {startedAt && (
                    <>
                      at <CustomDateField source="startedAt" isDateTime />
                    </>
                  )}
                </Fragment>
              );
            }}
          />
          <FunctionField
            label="End Details"
            render={(record: any) => {
              const { endedAt, endedBy } = record;

              // If both endedAt and endedBy are null, show a "-"
              if (!endedAt && !endedBy) {
                return <>-</>;
              }

              return (
                <Fragment>
                  {/* Show 'by {user}' if only endedBy is available */}
                  {endedBy && (
                    <>
                      by{" "}
                      <UpdatedByField
                        data={endedBy}
                        referenceSource="endedBy.id"
                      />
                    </>
                  )}

                  {/* Add a space between 'by {user}' and 'at {datetime}' only if both are available */}
                  {endedBy && endedAt && " "}

                  {/* Show 'at {datetime}' if only endedAt is available */}
                  {endedAt && (
                    <>
                      at <CustomDateField source="endedAt" isDateTime />
                    </>
                  )}
                </Fragment>
              );
            }}
          />
          <ReferenceField
            label="Route Plan Wave"
            source="busSchedulePlanId"
            reference="busSchedulePlans"
            link={false}
          >
            <ReferenceField
              source="busScheduleId"
              reference="busSchedules"
              link={false}
            >
              <TextField source="name" />
            </ReferenceField>
          </ReferenceField>
          <TextField
            source="driverAppVersionUsed"
            label="Driver App Version"
            emptyText="-"
          />
          <FunctionField
            label="Estimated Bus Arrival Time"
            render={(record: any) =>
              record.routeType === "to_school" && record.arrivalStatus ? (
                <CustomTimeField source="estimatedBusScheduleTime" />
              ) : (
                "-"
              )
            }
          />
          <FunctionField
            label="Arrival Status"
            render={(record: any) =>
              record.routeType === "to_school" && record.arrivalStatus ? (
                <StatusField source="arrivalStatus" />
              ) : (
                "-"
              )
            }
          />
          <StatusField label="Assignment Status" source="status" />
          <CustomBooleanField
            source="isDryRun"
            label="Dry Run Assignment"
            textForTrueValue="Yes"
            textForFalseValue="No"
          />
          <FunctionField
            label="Actions"
            render={(record: any) => (
              <ActionsMenu
                render={[
                  <ListShowButton record={record} />,
                  !record.isDryRun && <TimesheetShowButton {...record} />,
                ]}
              />
            )}
          />
        </Datagrid>
      </ListWrapper>
    </>
  );
};

export default AssignmentList;
